import S from 's-js';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { tzmomentMod } from 'lib/tzmoment';
import { LocalizationContext } from 'common/models/LocalizationContext';

export const enum DateRange {
    AllDates,
    ThisWeek,
    LastWeek,
    ThisMonth,
    LastMonth,
    ThisYear,
    LastYear,
    Custom
}

const DefaultDateRanges = [
    DateRange.ThisWeek,
    DateRange.LastWeek,
    DateRange.ThisMonth,
    DateRange.LastMonth,
    DateRange.ThisYear,
    DateRange.LastYear,
    DateRange.Custom
];

export interface DateRangeSelectorParams {
    range : DateRange;
    allowedRanges? : DateRange[];
    from? : TZDate;
    to? : TZDate;
}

export function DateRangeSelector(
    tzutils : TZUtils,
    localization : LocalizationContext,
    {
        range: _range,
        allowedRanges: _allowedRanges,
        from: _from,
        to: _to
    } : DateRangeSelectorParams
) {
    const allowedRanges = _allowedRanges || DefaultDateRanges;
    if (allowedRanges.indexOf(_range) === -1) throw new Error('Range ' + _range + ' not allowed for this DateRangeSelector.');

    const initialDates = getDatesFromRange(_range, tzutils),
        initialFrom = initialDates ? initialDates.from : _from || tzmomentMod(tzutils, _to || tzutils.now(), m => m.startOf('month')),
        initialTo = initialDates ? initialDates.to : _to || tzmomentMod(tzutils, initialFrom, m => m.add(1, 'month')),
        range = S.data(_range),
        fromDate = S.data(initialFrom),
        toDate = S.data(initialTo),
        // we want the custom "To" field to list the last day *included* in range (inclusive), not the first day *past* the range (exclusive)
        // so customToDate tracks toDate but one day earlier
        customToDate = (val? : TZDate) =>
            val === undefined
            ? tzmomentMod(tzutils, toDate(), m => m.subtract(1, 'day'))
            : (toDate(tzmomentMod(tzutils, val, m => m.add(1, 'day'))), val);

    // Set dates based on range value
    S.on(range, () => {
        const dates = getDatesFromRange(range(), tzutils);
        if (dates) {
            fromDate(dates.from);
            toDate(dates.to);
        }
    }, null, true);

    return {
        Range: range,
        AllowedRanges: allowedRanges,
        FromDate: fromDate,
        ToDate: toDate,
        CustomToDate: customToDate,
        Localization: localization
    };
}

function getDatesFromRange(range : DateRange, tzutils : TZUtils) {
    let from : TZDate,
        to : TZDate,
        now = tzutils.now();

    if (range === DateRange.AllDates) {
        // TH 01/10/2017 -- Any sufficiently wide date range will do.
        from = new TZDate('1000-01-01T00:00Z');
        to = new TZDate('3000-01-01T00:00Z');
    } else if (range === DateRange.ThisWeek || range === DateRange.LastWeek) {
        from = tzmomentMod(tzutils, now, m => m.startOf('week'));
        if (range === DateRange.LastWeek) from = tzmomentMod(tzutils, from, m => m.subtract(1, 'week'));
        to = tzmomentMod(tzutils, from, m => m.add(1, 'week'));
    } else if (range === DateRange.ThisMonth || range === DateRange.LastMonth) {
        from = tzmomentMod(tzutils, now, m => m.startOf('month'));
        if (range === DateRange.LastMonth) from = tzmomentMod(tzutils, from, m => m.subtract(1, 'month'));
        to = tzmomentMod(tzutils, from, m => m.add(1, 'month'));
    } else if (range === DateRange.ThisYear || range === DateRange.LastYear) {
        from = tzmomentMod(tzutils, now, m => m.startOf('year'));
        if (range === DateRange.LastYear) from = tzmomentMod(tzutils, from, m => m.subtract(1, 'year'));
        to = tzmomentMod(tzutils, from, m => m.add(1, 'year'));
    } else if (range === DateRange.Custom) {
        // Can't determine the dates when date range is custom.
        // Have to defer to user input instead.
        return null;
    } else {
        throw new Error('Unknown range value: ' + range);
    }

    return { from, to };
}

export type DateRangeSelector = ReturnType<typeof DateRangeSelector>;


// WEBPACK FOOTER //
// ./apps/portcall/daterangeselector/model.ts